import React, { useEffect } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import QuizRunner from "@/components/QuizRunner";
import SortRunner from "@/components/SortRunner";
import OrderRunner from "@/components/OrderRunner";
import MatchRunner from "@/components/MatchRunner";
import TracingRunner from "@/components/TracingRunner";
import { getSection } from "@/lib/content";
import { getState } from "@/lib/progress";
import { speak } from "@/lib/speech";
import { useSub } from "@/lib/subscription";
import { isFreeSection } from "@/lib/gating";
import { UpgradePrompt } from "@/components/UpgradeGate";

const tileBg = { yellow: "bg-amber-100 border-amber-300", blue: "bg-sky-100 border-sky-300", green: "bg-emerald-100 border-emerald-300", pink: "bg-pink-100 border-pink-300", purple: "bg-violet-100 border-violet-300", orange: "bg-orange-100 border-orange-300", sky: "bg-cyan-100 border-cyan-300" };

export default function SectionPage() {
  const { sectionId } = useParams();
  const [params, setParams] = useSearchParams();
  const { premium } = useSub();
  const section = getSection(sectionId);
  const modeId = params.get("mode");
  const mode = section?.modes.find(m => m.id === modeId);

  useEffect(() => { if (section && !mode) speak(`${section.title}. Pick an activity!`); }, [section, mode]);

  if (!section) return <Navigate to="/learn" replace />;

  const locked = !premium && !isFreeSection(section.id);
  if (locked) return (<><TopBar title={section.title} backTo="/learn" /><UpgradePrompt title={section.title} kind="section" /></>);

  if (mode) {
    const common = { title: mode.title, category: section.id, modeId: mode.id, build: mode.build, backTo: section.path };
    const Runner = { quiz: QuizRunner, sort: SortRunner, order: OrderRunner, match: MatchRunner }[mode.kind];
    return (
      <>
        <TopBar title={`${section.short} · ${mode.title}`} backTo={section.path} />
        {mode.kind === "trace" ? <TracingRunner modeId={mode.id} backTo={section.path} /> : <Runner key={mode.id} {...common} />}
      </>
    );
  }

  const done = getState().modesCompleted[section.id] || {};
  return (
    <>
      <TopBar title={section.title} backTo="/learn" />
      <div className="ll-bg min-h-screen pb-28 md:pb-8">
        <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
          <div className={`ll-card ${tileBg[section.color]} p-6 sm:p-8 flex items-center gap-5`} data-testid="section-hero">
            <img src={section.image} alt="" className="w-24 h-24 sm:w-36 sm:h-36 object-contain ll-float" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: section.emoji, className: "text-7xl" })); }} />
            <div>
              <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Learning Section</div>
              <h1 className="font-display font-bold text-3xl sm:text-5xl text-purple-900 mt-1">{section.title}</h1>
              <p className="text-purple-700/80 font-semibold mt-1">{section.desc} · {section.modes.length} activities</p>
            </div>
          </div>
          <h2 className="font-display font-bold text-2xl text-purple-900 mt-8">Pick an activity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {section.modes.map(m => (
              <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setParams({ mode: m.id })} className="ll-tile bg-white text-left flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center text-3xl font-display font-bold text-purple-700 shrink-0">{m.emoji}</div>
                <div className="flex-1">
                  <div className="font-display font-bold text-lg text-purple-900">{m.title}</div>
                  <div className="text-purple-700/80 text-sm font-semibold">{m.desc}</div>
                </div>
                {done[m.id] > 0 && <div className="text-xl" title={`Completed ${done[m.id]}x`}>✅</div>}
              </button>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}
